// libs
import * as React from 'react'

// interfaces / constants
import { IErrorResponse } from 'src/api/interfaces/errors'
import { CLASS_PREFIX } from 'src/constants/'
import { POST_DISPLAY_MODE_PREVIEW, PURPOSE_BASIC } from 'src/interfaces/posts'

// classes
import Avatar from 'src/components/avatar/avatar'
import LegacyButton, {
    BUTTON_TYPE_ROUNDMID
} from 'src/components/forms/button/button'
import Icon from 'src/components/forms/icon/icon'
import LinkPreview, {
    IPropsData as ILinkpreviewProps
} from 'src/components/post/shared/ingredients/link_preview/link_preview'
import TextInput, { TextInputHandle } from 'src/components/text_input/text_input'
import {
    THUMBNAIL_SIZE_TYPE_LARGE_MEDIUM_LARGE,
    THUMBNAIL_SIZE_TYPE_XLARGE,
    THUMBNAIL_SIZE_TYPE_XSMALL
} from 'src/components/thumbnail/thumbnail'
import Uploader from 'src/components/uploader/uploader'

// helpers
import {
    CommentFragmentFragment,
    OglinkFragmentFragment,
    ShapeSponsorshipLevel
} from '@lopo-tech/lopo-api'
import { textResources } from 'src/lang/de'
import { getSponsorBadge } from 'src/utils/badges'
import { COLOR_TYPE_GRAY_TINT_9 } from 'src/utils/color'
import UploadedImage from '../uploader/uploaded_image'
import './media_input.scss'

const cls = CLASS_PREFIX + 'media-input'
const clsCancelEditDesktop = `${cls}__cancel-edit--desktop`
const clsMobileWrapper = `${cls}__mobile-wrapper`
const labels = textResources.comments

const MODE_TYPE_CREATE = 'create'
export const MODE_TYPE_EDIT = 'edit'

export type ModeType = 'create' | 'edit';

const MAX_IMAGE_NUMBER_DEFAULT = 10

interface IProps {
  cancel?: () => void;
  cancelEditMessage?: string;
  cancelEditComponent?: JSX.Element;
  disabled: boolean;
  attachements?: CommentFragmentFragment['attachments'];
  oglink?: OglinkFragmentFragment | null;
  maxImageNumber?: number;
  mediaMaxImagesError: string;
  message?: string | null;
  mode?: ModeType;
  onError?: (error: IErrorResponse) => void;
  placeholder?: string;
  refProp?: (ref: TextInputHandle) => void;
  sponsorLevel?: ShapeSponsorshipLevel | null;
  submit?: (str: string, images?: string[], oglinkId?: string) => void;
  update?: (message: string, oldImages: string[], newImages: string[], oglinkId?: string) => void;
  userImageUrl: string | undefined;
}

interface IState {
  errorMessage: string | null;
  newAttachements: string[];
  oglink?: OglinkFragmentFragment | null;
  message: string;
  mode: ModeType;
  areUploadedAttachementsChanged: boolean;
  uploadedAttachements: CommentFragmentFragment['attachments'];
  originalOgLink?: OglinkFragmentFragment | null;
  originalMessage: string;
}

export default class MediaInput extends React.PureComponent<IProps, IState> {
  public constructor (props: IProps) {
    super(props)
    const { attachements, message, mode, oglink } = props
    this.textInputChange = this.textInputChange.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.handleUpdate = this.handleUpdate.bind(this)
    this.onDetectLink = this.onDetectLink.bind(this)
    this.onImageAdded = this.onImageAdded.bind(this)
    this.onImageDeleted = this.onImageDeleted.bind(this)
    this.onImageUploadError = this.onImageUploadError.bind(this)
    this.onRemoveLinkPreview = this.onRemoveLinkPreview.bind(this)
    this.renderCancelEditOnMobile = this.renderCancelEditOnMobile.bind(this)
    this.renderCancelEditOnDesktop = this.renderCancelEditOnDesktop.bind(this)
    this.renderImage = this.renderImage.bind(this)
    this.renderLinkPreview = this.renderLinkPreview.bind(this)
    this.renderSubmitButton = this.renderSubmitButton.bind(this)
    this.renderUpdateButton = this.renderUpdateButton.bind(this)
    this.renderUploadButton = this.renderUploadButton.bind(this)
    this.state = {
      errorMessage: null,
      newAttachements: [],
      oglink,
      message: message || '',
      mode: mode || MODE_TYPE_CREATE,
      areUploadedAttachementsChanged: false,
      uploadedAttachements: attachements || [],
      originalOgLink: oglink,
      originalMessage: message || ''
    }
  }

  public componentDidUpdate (prevProps: IProps) {
    const { attachements, message, mode } = this.props
    if (prevProps.attachements !== attachements && attachements) {
      this.setState({
        uploadedAttachements: attachements,
        areUploadedAttachementsChanged: false
      })
    }
    if (prevProps.message !== message && message) {
      this.setState({ message })
    }
    if (prevProps.mode !== mode && mode) {
      this.setState({ mode, areUploadedAttachementsChanged: false })
    }
  }

  public render () {
    const { disabled, placeholder, refProp, sponsorLevel, userImageUrl } =
      this.props
    const {
      errorMessage,
      newAttachements,
      uploadedAttachements,
      message,
      mode,
      oglink: linkPreviewParams
    } = this.state
    return (
      <div className={cls}>
        {mode === MODE_TYPE_EDIT && this.renderCancelEditOnMobile()}
        {(newAttachements.length > 0 ||
          uploadedAttachements.length ||
          linkPreviewParams) && (
          <div className={clsMobileWrapper}>
            {(newAttachements.length > 0 || uploadedAttachements.length > 0) && (
              <div className={cls + '__images--mobile'}>
                <div className={cls + '__images'}>
                  {[
                    ...uploadedAttachements.map(this.renderImage),
                    ...newAttachements.map(this.renderImage)
                  ]}
                </div>
              </div>
            )}
            {linkPreviewParams && (
              <div className={cls + '__link-preview--mobile'}>
                {this.renderLinkPreview()}
              </div>
            )}
          </div>
        )}
        {errorMessage && (
          <div className={`${cls}__error-message`}> {errorMessage} </div>
        )}

        <div className={cls + '__wrapper'}>
          <div className={cls + '__avatar'}>
            <Avatar
              image={userImageUrl}
              size={THUMBNAIL_SIZE_TYPE_LARGE_MEDIUM_LARGE}
              withBorder
              rounded={false}
              badge={sponsorLevel && getSponsorBadge(sponsorLevel)}
            />
          </div>
          <div className={cls + '__form-wrapper'}>
            <div className={cls + '__form'}>
              <div className={cls + '__input-container'}>
                <div className={cls + '__avatar--mobile'}>
                  <Avatar
                    image={userImageUrl}
                    size={THUMBNAIL_SIZE_TYPE_XSMALL}
                    withBorder
                    rounded={false}
                    badge={sponsorLevel && getSponsorBadge(sponsorLevel)}
                  />
                </div>
                <TextInput
                  className={cls + '__textarea'}
                  disabled={disabled}
                  expandable={true}
                  initialRows={1}
                  maxHeight={60}
                  name="media-input"
                  onChange={this.textInputChange}
                  placeholder={placeholder}
                  value={message}
                  onDetectLink={this.onDetectLink}
                  showEmoji
                  ref={refProp}
                >
                  {this.renderUploadButton()}
                </TextInput>
              </div>
              {mode === MODE_TYPE_CREATE && this.renderSubmitButton()}
              {mode === MODE_TYPE_EDIT && this.renderUpdateButton()}
            </div>
            {(newAttachements.length > 0 || uploadedAttachements.length > 0) && (
              <div className={cls + '__images--desktop'}>
                <div className={cls + '__images'}>
                  {[
                    ...uploadedAttachements.map(this.renderImage),
                    ...newAttachements.map(this.renderImage)
                  ]}
                </div>
              </div>
            )}
            {linkPreviewParams && (
              <div className={cls + '__link-preview--desktop'}>
                {this.renderLinkPreview()}
              </div>
            )}
            {mode === MODE_TYPE_EDIT && this.renderCancelEditOnDesktop()}
          </div>
        </div>
      </div>
    )
  }

  private renderLinkPreview () {
    const { oglink } = this.state
    if (!oglink) {
      return null
    }
    const {
      author,
      description,
      image,
      title,
      url
    } = oglink
    const linkPreviewData: ILinkpreviewProps = {
      author,
      description,
      imageUrl: image?.previewUrl,
      onRemove: this.onRemoveLinkPreview,
      showRemoveIcon: true,
      title,
      url
    }
    return (
      <LinkPreview
        data={linkPreviewData}
        displayMode={POST_DISPLAY_MODE_PREVIEW}
        purpose={PURPOSE_BASIC}
      />
    )
  }

  private onRemoveLinkPreview () {
    if (this.state.oglink) {
      this.setState({ oglink: undefined })
    }
  }

  private onDetectLink (link: CommentFragmentFragment['oglink'] | undefined): void {
    this.setState({ oglink: link })
  }

  private renderCancelEditOnMobile (): JSX.Element {
    const { cancelEditMessage } = this.props
    return (
      <button
        className={`${cls}__cancel-edit--mobile`}
        onClick={this.props.cancel}
      >
        {cancelEditMessage}
        <div className={`${cls}__cancel-edit--mobile-close`}>
          <Icon aria-hidden="true" name={'thin-x'} />
        </div>
      </button>
    )
  }

  private renderCancelEditOnDesktop (): JSX.Element {
    const { cancelEditComponent } = this.props

    return <div className={clsCancelEditDesktop}>{cancelEditComponent}</div>
  }

  private renderSubmitButton (): JSX.Element {
    return (
      <LegacyButton
        className={cls + '__submit'}
        color={COLOR_TYPE_GRAY_TINT_9}
        icon={'paper-plane-filled'}
        disabled={this.props.disabled || this.state.message.trim() === ''}
        type="button"
        variant={BUTTON_TYPE_ROUNDMID}
        onClick={this.handleSubmit}
        title={labels.submit}
      />
    )
  }

  private renderUploadButton (): JSX.Element {
    const { disabled } = this.props
    const { newAttachements: attachements } = this.state
    return (
      <Uploader
        activeClassName={`${cls}__uploader--active`}
        rejectClassName={`${cls}__uploader--reject`}
        onError={this.onImageUploadError}
        onImageAdded={this.onImageAdded}
        {...attachements}
      >
        <LegacyButton
          className={cls + '__camera'}
          disabled={disabled}
          icon={'camera-filled'}
          type="button"
          title={labels.attachImage}
        />
      </Uploader>
    )
  }

  private renderUpdateButton (): JSX.Element {
    const { disabled } = this.props
    const {
      newAttachements,
      oglink: linkPreviewParams,
      message,
      areUploadedAttachementsChanged,
      originalOgLink: originalLinkPreviewParams,
      originalMessage
    } = this.state

    const commentChanged =
      message !== originalMessage ||
      newAttachements.length ||
      areUploadedAttachementsChanged ||
      linkPreviewParams !== originalLinkPreviewParams

    return (
      <LegacyButton
        className={cls + '__update'}
        disabled={disabled || message.trim() === '' || !commentChanged}
        color={COLOR_TYPE_GRAY_TINT_9}
        icon={'check'}
        variant={BUTTON_TYPE_ROUNDMID}
        onClick={this.handleUpdate}
        title={labels.update}
      />
    )
  }

  private renderImage (image: string | CommentFragmentFragment['attachments'][number], index: number) {
    const indexWithOffset = typeof image === 'string' ? index + this.state.uploadedAttachements.length : index
    return (
      <UploadedImage
        onDelete={this.onImageDeleted}
        url={typeof image === 'string' ? image : image.file.previewUrl}
        key={indexWithOffset}
        index={indexWithOffset}
        size={THUMBNAIL_SIZE_TYPE_XLARGE}
      />
    )
  }

  private checkImageNumber () {
    const { newAttachements, uploadedAttachements } = this.state
    const maxImageNumber =
      this.props.maxImageNumber || MAX_IMAGE_NUMBER_DEFAULT
    const { mediaMaxImagesError } = this.props
    if (newAttachements.length + uploadedAttachements.length > maxImageNumber) {
      const slicedAttachements = newAttachements.slice(0, maxImageNumber - uploadedAttachements.length)
      this.setState({
        errorMessage: mediaMaxImagesError,
        newAttachements: slicedAttachements
      })
    } else {
      this.setState({ errorMessage: null })
    }
  }

  private handleUpdate (): void {
    const { update } = this.props
    const { uploadedAttachements, newAttachements, message, mode, oglink } = this.state
    const oglinkId = oglink?.id
    if (mode === MODE_TYPE_EDIT && update) {
      update(
        message,
        uploadedAttachements.map((attachement) => attachement.id),
        newAttachements,
        oglinkId
      )
    }
    this.setState({
      errorMessage: null,
      newAttachements: [],
      oglink: undefined,
      message: '',
      mode: MODE_TYPE_CREATE
    })
  }

  private handleSubmit (): void {
    const { submit, disabled } = this.props
    const { newAttachements, message, mode } = this.state
    const isDisabled = disabled || message.trim() === ''
    const oglinkId = this.state.oglink?.id
    if (!isDisabled && mode === MODE_TYPE_CREATE && submit) {
      submit(
        message,
        newAttachements.length ? newAttachements : undefined,
        oglinkId
      )
    }
    this.setState({
      errorMessage: null,
      newAttachements: [],
      oglink: undefined,
      message: '',
      mode: MODE_TYPE_CREATE
    })
  }

  private onImageAdded (addedImages: string[]) {
    const { newAttachements } = this.state
    const newState = {
      newAttachements: (newAttachements || []).concat(addedImages)
    }
    this.setState({ ...newState, errorMessage: null })
    this.checkImageNumber()
  }

  private onImageDeleted (index: number) {
    const { uploadedAttachements, newAttachements } = this.state
    if (uploadedAttachements.length || newAttachements.length) {
      // we always render the already uploaded images first
      if (index < uploadedAttachements.length) {
        this.setState({
          uploadedAttachements: uploadedAttachements
            .slice(0, index)
            .concat(uploadedAttachements.slice(index + 1)),
          areUploadedAttachementsChanged: true
        })
      } else {
        const offsetIndex = index - uploadedAttachements.length
        this.setState({
          newAttachements: newAttachements
            .slice(0, offsetIndex)
            .concat(newAttachements.slice(offsetIndex + 1))
        })
      }
    }
    this.checkImageNumber()
  }

  private onImageUploadError (error: IErrorResponse) {
    const { onError } = this.props
    this.setState({ errorMessage: textResources.shared.imageAddError })
    onError?.(error)
  }

  private textInputChange (value: string) {
    this.setState({ message: value })
  }
}
